/**
 * Theme Utilities for Multi-Tenancy
 *
 * This module provides utilities for dynamically updating CSS variables
 * based on the current tenant's theme configuration.
 *
 * USAGE:
 * Called automatically by TenantProvider when tenant changes.
 * Do not call directly in components.
 *
 * @see src/context/TenantContext.tsx for integration
 * @see src/index.css for CSS variable definitions
 * @see CLAUDE.md Architecture Standards for theming rules
 */

import { TenantConfig, TenantTheme, DEFAULT_THEME } from '@/types/tenant.types';

/**
 * CSS variable names mapped to theme properties
 */
const CSS_VARIABLE_MAP: Record<keyof TenantTheme, string> = {
  primaryColor: '--brand-primary',
  secondaryColor: '--brand-secondary',
  accentColor: '--brand-accent',
  darkColor: '--brand-dark',
  successColor: '--brand-success',
  dangerColor: '--brand-danger',
  warningColor: '--brand-warning',
};

/**
 * Convert a hex color to RGB values string
 *
 * @param hex - Hex color string (e.g., "#c5a065" or "c5a065")
 * @returns RGB values as SPACE-separated string (e.g., "197 160 101")
 *
 * IMPORTANT: Uses space-separated format for modern CSS rgb() syntax.
 * rgb(197 160 101 / 0.5) is valid, rgb(197, 160, 101 / 0.5) is NOT
 *
 * @example
 * hexToRgb('#c5a065') // Returns "197 160 101"
 */
export function hexToRgb(hex: string): string {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, '');

  // Parse hex values
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  // Handle invalid hex
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    console.warn(`[Theme] Invalid hex color: ${hex}`);
    return '0 0 0';
  }

  return `${r} ${g} ${b}`;
}

/**
 * Apply a tenant's theme to the document
 *
 * Updates CSS custom properties on document.documentElement
 * to reflect the tenant's color scheme.
 *
 * @param tenant - The tenant configuration to apply
 *
 * @example
 * // In TenantProvider
 * useEffect(() => {
 *   if (tenant) {
 *     applyTenantTheme(tenant);
 *   }
 * }, [tenant]);
 */
export function applyTenantTheme(tenant: TenantConfig): void {
  if (!tenant?.theme) {
    console.warn('[Theme] No theme in tenant config, using defaults');
    applyDefaultTheme();
    return;
  }

  const root = document.documentElement;
  const { theme } = tenant;

  console.log(`[Theme] Applying theme for: ${tenant.companyName}`);

  // Apply each theme color as a CSS variable
  Object.entries(CSS_VARIABLE_MAP).forEach(([themeKey, cssVar]) => {
    const colorValue = theme[themeKey as keyof TenantTheme];
    if (colorValue) {
      root.style.setProperty(cssVar, colorValue);
    }
  });

  // Set RGB versions for colors that need Tailwind opacity support
  // These enable classes like bg-brand-secondary/50, border-brand-primary/30
  if (theme.primaryColor) {
    root.style.setProperty('--brand-primary-rgb', hexToRgb(theme.primaryColor));
  }
  if (theme.secondaryColor) {
    root.style.setProperty('--brand-secondary-rgb', hexToRgb(theme.secondaryColor));
  }
  if (theme.darkColor) {
    root.style.setProperty('--brand-dark-rgb', hexToRgb(theme.darkColor));
  }
  if (theme.successColor) {
    root.style.setProperty('--brand-success-rgb', hexToRgb(theme.successColor));
  }
  if (theme.dangerColor) {
    root.style.setProperty('--brand-danger-rgb', hexToRgb(theme.dangerColor));
  }
  if (theme.warningColor) {
    root.style.setProperty('--brand-warning-rgb', hexToRgb(theme.warningColor));
  }

  // Update meta theme-color for PWA/mobile browsers
  updateMetaThemeColor(theme.primaryColor || DEFAULT_THEME.primaryColor);

  console.log('[Theme] Theme applied successfully');
}

/**
 * Apply default theme colors
 *
 * Resets CSS variables to the default values defined in DEFAULT_THEME.
 * Called when tenant is null or has no theme.
 */
export function applyDefaultTheme(): void {
  console.log('[Theme] Applying default theme');

  const root = document.documentElement;

  Object.entries(CSS_VARIABLE_MAP).forEach(([themeKey, cssVar]) => {
    const defaultValue = DEFAULT_THEME[themeKey as keyof TenantTheme];
    if (defaultValue) {
      root.style.setProperty(cssVar, defaultValue);
    }
  });

  // Set RGB versions for Tailwind opacity support
  root.style.setProperty('--brand-primary-rgb', hexToRgb(DEFAULT_THEME.primaryColor));
  root.style.setProperty('--brand-secondary-rgb', hexToRgb(DEFAULT_THEME.secondaryColor));
  root.style.setProperty('--brand-dark-rgb', hexToRgb(DEFAULT_THEME.darkColor || '#0f1521'));
  root.style.setProperty('--brand-success-rgb', hexToRgb(DEFAULT_THEME.successColor || '#10b981'));
  root.style.setProperty('--brand-danger-rgb', hexToRgb(DEFAULT_THEME.dangerColor || '#ef4444'));
  root.style.setProperty('--brand-warning-rgb', hexToRgb(DEFAULT_THEME.warningColor || '#f59e0b'));

  // Update meta theme-color
  updateMetaThemeColor(DEFAULT_THEME.primaryColor);
}

/**
 * Update the meta theme-color tag for PWA/mobile browsers
 *
 * This affects the browser chrome color on mobile devices
 * and the title bar in PWA mode.
 *
 * @param color - Hex color to apply
 */
export function updateMetaThemeColor(color: string): void {
  let metaTag = document.querySelector('meta[name="theme-color"]');

  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute('name', 'theme-color');
    document.head.appendChild(metaTag);
  }

  metaTag.setAttribute('content', color);
}

/**
 * Get the current value of a CSS variable
 *
 * @param variableName - CSS variable name (e.g., '--brand-primary')
 * @returns The current value or empty string if not set
 *
 * @example
 * const primary = getCssVariable('--brand-primary');
 * // Returns "#1a2238"
 */
export function getCssVariable(variableName: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
}

/**
 * Set a single CSS variable
 *
 * @param variableName - CSS variable name (e.g., '--brand-primary')
 * @param value - Value to set
 *
 * @example
 * setCssVariable('--brand-primary', '#ff0000');
 */
export function setCssVariable(variableName: string, value: string): void {
  document.documentElement.style.setProperty(variableName, value);
}

/**
 * Generate a lighter variant of a hex color
 *
 * @param hex - Base hex color
 * @param percent - Percentage to lighten (0-100)
 * @returns Lightened hex color
 */
export function lightenColor(hex: string, percent: number): string {
  const cleanHex = hex.replace(/^#/, '');
  const num = parseInt(cleanHex, 16);

  let r = (num >> 16) + Math.round(2.55 * percent);
  let g = ((num >> 8) & 0x00ff) + Math.round(2.55 * percent);
  let b = (num & 0x0000ff) + Math.round(2.55 * percent);

  r = Math.min(255, Math.max(0, r));
  g = Math.min(255, Math.max(0, g));
  b = Math.min(255, Math.max(0, b));

  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/**
 * Generate a darker variant of a hex color
 *
 * @param hex - Base hex color
 * @param percent - Percentage to darken (0-100)
 * @returns Darkened hex color
 */
export function darkenColor(hex: string, percent: number): string {
  const cleanHex = hex.replace(/^#/, '');
  const num = parseInt(cleanHex, 16);

  let r = (num >> 16) - Math.round(2.55 * percent);
  let g = ((num >> 8) & 0x00ff) - Math.round(2.55 * percent);
  let b = (num & 0x0000ff) - Math.round(2.55 * percent);

  r = Math.min(255, Math.max(0, r));
  g = Math.min(255, Math.max(0, g));
  b = Math.min(255, Math.max(0, b));

  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/**
 * Check if a color is light or dark
 *
 * Useful for determining text color on dynamic backgrounds.
 *
 * @param hex - Hex color to check
 * @returns true if the color is considered "light"
 *
 * @example
 * isLightColor('#ffffff') // true
 * isLightColor('#000000') // false
 */
export function isLightColor(hex: string): boolean {
  const cleanHex = hex.replace(/^#/, '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5;
}

/**
 * Get contrasting text color for a background
 *
 * @param backgroundHex - Background color hex
 * @returns "#ffffff" for dark backgrounds, "#000000" for light backgrounds
 */
export function getContrastColor(backgroundHex: string): string {
  return isLightColor(backgroundHex) ? '#000000' : '#ffffff';
}

/**
 * Initialize theme on module load
 *
 * This ensures the default Techery colors are applied immediately
 * when the app first loads, before TenantProvider renders.
 * This prevents a flash of incorrect colors.
 */
if (typeof window !== 'undefined') {
  applyDefaultTheme();
}
